import React, { useRef } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useGSAP } from '@gsap/react';

gsap.registerPlugin(ScrollTrigger);

const Philosophy: React.FC = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const horizontalRef = useRef<HTMLDivElement>(null);
  const progressBarRef = useRef<HTMLDivElement>(null);

  useGSAP(() => {
    // Check refs exist
    if (!horizontalRef.current || !containerRef.current) return;
    
    // Calculate exact scroll distance needed
    const getScrollAmount = () => {
        return horizontalRef.current!.scrollWidth - window.innerWidth;
    };

    const scrollAmount = getScrollAmount();

    // 1. Horizontal Scroll Tween
    const scrollTween = gsap.to(horizontalRef.current, {
        x: -scrollAmount,
        ease: "none",
        scrollTrigger: {
        trigger: containerRef.current,
        pin: true,
        scrub: 1,
        start: "top top",
        end: () => `+=${scrollAmount}`,
        invalidateOnRefresh: true,
        anticipatePin: 1,
        }
    });

    // 2. Progress Bar
    if (progressBarRef.current) {
        gsap.to(progressBarRef.current, {
        scaleX: 1,
        ease: "none",
        scrollTrigger: {
            trigger: containerRef.current,
            start: "top top",
            end: () => `+=${scrollAmount}`,
            scrub: 1,
        }
        });
    }

    // 3. Card Specific Animations (Trigger when in center)
    const cards = gsap.utils.toArray<HTMLElement>('.philosophy-card');
    cards.forEach((card, i) => {
        const img = card.querySelector('.philosophy-img');
        const overlay = card.querySelector('.card-overlay');
        const texts = card.querySelectorAll('.card-text-item'); // Select all text elements with this class

        const tl = gsap.timeline({
            scrollTrigger: {
                trigger: card,
                containerAnimation: scrollTween,
                start: "left 60%", // Triggers slightly before center
                end: "right 40%",
                toggleActions: "play reverse play reverse",
            }
        });

        // Image Reveal
        tl.to(img, {
            filter: "grayscale(0%)",
            scale: 1.05,
            duration: 0.8,
            ease: "power2.out"
        }, 0);

        // Overlay Lighten
        tl.to(overlay, {
            backgroundColor: 'rgba(0,0,0,0.2)',
            duration: 0.8,
            ease: "power2.out"
        }, 0);

        // Text Reveal
        tl.to(texts, {
            y: 0,
            opacity: 1,
            duration: 0.8,
            stagger: 0.1,
            ease: "power3.out"
        }, 0.1);

    });

  }, { scope: containerRef });

  const cards = [
    {
      title: "Materiality",
      subtitle: "Solid Teak",
      desc: "Unlike the mass of cheaper foil veneered options, our furniture is made from solid teak wood, carefully selected from renewable sources.",
      img: "https://images.unsplash.com/photo-1533090161767-e6ffed986c88?q=80&w=2669&auto=format&fit=crop"
    },
    {
      title: "Standards",
      subtitle: "Excellence",
      desc: "We set industry standards for Design, Craftsmanship and Service. Our aim is to create pieces as practical and enduring as they are beautiful.",
      img: "https://images.unsplash.com/photo-1533090481720-856c6e3c1fdc?q=80&w=2576&auto=format&fit=crop"
    },
    {
      title: "Commitment",
      subtitle: "Everyday",
      desc: "Words such as quality in service, delivery, styling, and superb products are not merely slogans — they are our everyday approach.",
      img: "https://images.unsplash.com/photo-1617104424032-b9bd6972d0e4?q=80&w=2670&auto=format&fit=crop"
    },
    {
      title: "Leadership",
      subtitle: "Vision",
      desc: "With a shared vision of quality in our daily work, D'mans is demonstrating everyday what it takes to be a leader in the industry.",
      img: "https://images.unsplash.com/photo-1513694203232-719a280e022f?q=80&w=2669&auto=format&fit=crop"
    }
  ];

  return (
    <section id="philosophy" ref={containerRef} className="bg-diwan-800 relative h-screen w-full overflow-hidden">
      
      {/* Fixed Title / Overlay Layer */}
      <div className="absolute left-6 top-24 md:left-20 md:top-24 z-30 pointer-events-none text-white drop-shadow-lg">
          <h3 className="font-serif text-4xl md:text-6xl mb-2 tracking-wide">Philosophy</h3>
          <p className="font-sans text-diwan-gold text-xs tracking-[0.3em] uppercase pl-1">The D'mans Standard</p>
      </div>

      {/* Progress Bar Layer - Olive Theme */}
      <div className="absolute bottom-0 left-0 w-full h-1 bg-white/10 z-40">
          <div ref={progressBarRef} className="h-full bg-diwan-gold origin-left scale-x-0 w-full"></div>
      </div>

      {/* Sliding Container */}
      <div ref={horizontalRef} className="flex flex-row w-[400%] h-full">
        {cards.map((card, index) => (
          <div key={index} className="philosophy-card w-1/4 h-full flex-shrink-0 relative border-r border-white/10 overflow-hidden">
            
            {/* Background Image */}
            <div className="absolute inset-0 z-0">
                 {/* Overlay - manipulated by GSAP */}
                 <div className="card-overlay absolute inset-0 bg-black/50 transition-colors duration-700 z-10"></div>
                 <img 
                    src={card.img} 
                    alt={card.title} 
                    className="philosophy-img w-full h-full object-cover grayscale scale-100" 
                 />
            </div>
            
            {/* Content Container */}
            <div className="absolute bottom-0 left-0 p-8 md:p-24 z-20 max-w-2xl pointer-events-none">
              <span className="card-text-item text-diwan-gold text-xs tracking-widest uppercase mb-2 block opacity-0 translate-y-8">
                  {card.subtitle}
              </span>
              
              <div className="overflow-hidden mb-6">
                  <h4 className="card-text-item text-5xl md:text-8xl font-serif text-white opacity-0 translate-y-8">
                    {card.title}
                  </h4>
              </div>
              
              <div className="overflow-hidden">
                  <p className="card-text-item text-white/90 font-sans text-sm md:text-lg leading-relaxed border-l-2 border-diwan-gold pl-6 ml-1 opacity-0 translate-y-8">
                    {card.desc}
                  </p>
              </div>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};

export default Philosophy;